import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Checkbox,
  Label,
  Tooltip,
  Typography,
  Badge,
  Button,
  Card,
  toast,
} from "@altimateai/lego";
import { Stack, Spinner } from "@altimateai/lego";
import dayjs from "dayjs";
import { IconRefresh, IconPlayerPlay, IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { getIconByDatastoreType } from "../DatastoreImage";
import { DatastoreTypeEnum } from "../../types";
import { TestState, API_RESULT_TIMEOUT } from "../../constants";
import {
  triggerDbtCloudSync,
  updateDbtCloudSchedule,
  testDbtCloudConnection,
} from "@/helpers/apis";
import { DbtCloudConnectionResponse, DBT_CLOUD_QUERY_KEYS } from "../../types";
import { getLastSyncInfo, getEnabledEnvironmentCount } from "./dbtCloudCardUtils";

interface DbtCloudCardProps {
  connection: DbtCloudConnectionResponse;
  syncOpAllowed: boolean;
}

const DbtCloudCard = ({ connection, syncOpAllowed }: DbtCloudCardProps): JSX.Element => {
  const queryClient = useQueryClient();
  const [isChecked, setIsChecked] = useState(connection?.schedule?.is_active || false);
  const [testState, setTestState] = useState<TestState>(TestState.Uninitialized);
  const [showSyncing, setShowSyncing] = useState(false);

  const { mutate: runSync } = useMutation({
    mutationFn: () => triggerDbtCloudSync(connection.id),
    onSuccess: () => {
      setShowSyncing(false);
      queryClient.invalidateQueries({ queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTIONS] });
    },
    onError: () => setShowSyncing(false),
  });

  const { mutate: toggleSchedule } = useMutation({
    mutationFn: () => updateDbtCloudSchedule(connection.id, { is_active: !isChecked }),
    onSuccess: () => {
      const newState = !isChecked;
      setIsChecked(newState);
      queryClient.invalidateQueries({ queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTIONS] });
      toast({
        title: newState ? "Sync enabled" : "Sync disabled",
        description: newState
          ? "Scheduled syncs will now run automatically"
          : "Scheduled syncs will not run until sync is enabled",
      });
    },
  });

  const { mutate: runTestConnection, isPending: isTesting } = useMutation({
    mutationFn: () => testDbtCloudConnection(connection.id),
    onSuccess: response => {
      if (response?.valid) {
        setTestState(TestState.Success);
        toast({
          title: "Connection successful",
          description: `Connected to ${response?.account_name || connection.name}`,
        });
      } else {
        setTestState(TestState.Fail);
        toast({
          variant: "destructive",
          title: "Connection failed",
          description: response?.message || "Unable to connect",
        });
      }
      setTimeout(() => setTestState(TestState.Uninitialized), API_RESULT_TIMEOUT.CONNECTION_TEST);
    },
    onError: (err: Error) => {
      setTestState(TestState.Fail);
      toast({
        variant: "destructive",
        title: "Connection test failed",
        description: err.message || "Unable to test connection",
      });
      setTimeout(() => setTestState(TestState.Uninitialized), API_RESULT_TIMEOUT.CONNECTION_TEST);
    },
  });

  const handleSync = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowSyncing(true);
    runSync();
  };

  const handleTestConnection = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    runTestConnection();
  };

  const handleToggleSwitch = (e: React.MouseEvent | unknown) => {
    if (e && typeof e === "object" && "stopPropagation" in e) {
      (e as React.MouseEvent).stopPropagation();
      (e as React.MouseEvent).preventDefault();
    }
    toggleSchedule();
  };

  const { lastSyncAt, lastSyncStatus } = getLastSyncInfo(connection);
  const enabledEnvCount = getEnabledEnvironmentCount(connection);
  const description =
    enabledEnvCount > 0
      ? `${enabledEnvCount} environment${enabledEnvCount > 1 ? "s" : ""} enabled`
      : "No environments enabled";

  const getTestIcon = () => {
    if (isTesting) return <Spinner size="sm" />;
    if (testState === TestState.Success)
      return <IconCircleCheck size={20} className="al-text-[#01cd8c]" />;
    if (testState === TestState.Fail)
      return <IconCircleX size={20} className="al-text-[#ee140f]" />;
    return <IconPlayerPlay size={20} />;
  };

  return (
    <Link to={`/settings/datastores/dbt-cloud/${connection.id}`} className="al-block">
      <Card className="al-cursor-pointer hover:al-shadow-md al-transition-shadow">
        <Stack direction="row" justify="between" align="center" className="al-mb-2 al-px-5 al-pt-4">
          <Stack direction="row" justify="between" align="center">
            {getIconByDatastoreType({
              datastoreType: DatastoreTypeEnum.DBT_CLOUD,
              className: "al-rounded al-w-14 al-h-14",
            })}
            <div className="al-ms-2">
              <Typography variant="section-heading">{connection.name}</Typography>
              <Typography size="sm" className="al-text-brand">
                dbt Cloud
              </Typography>
            </div>
          </Stack>

          <Stack
            direction="column"
            align="end"
            className="al-w-[50px]"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Stack direction="row" align="center" justify="between">
              <Label
                className="al-pointer-events-none al-text-[10px] al-leading-[15px] al-text-[#082247] al-whitespace-nowrap"
                htmlFor={"flexSwitchCheckDefault" + connection.id}
              >
                Enable Sync
              </Label>
              <Tooltip title={!syncOpAllowed ? "Insufficient permissions for enabling sync" : ""}>
                <Checkbox
                  role="switch"
                  id={"flexSwitchCheckDefault" + connection.id}
                  checked={isChecked}
                  onCheckedChange={handleToggleSwitch}
                  disabled={!syncOpAllowed}
                />
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justify="between"
          align="center"
          className="al-mb-2 al-text-gray al-px-5"
        >
          {description}
        </Stack>

        <Stack
          direction="row"
          align="center"
          className="al-py-1 al-px-[14px] al-pl-5 al-rounded-b-[20px] al-bg-[#f5f6f7] al-row-[3/4]"
        >
          <Stack direction="row" justify="between" align="center" className="al-mt-1 al-w-full">
            {testState === TestState.Success && (
              <Typography size="sm" className="al-text-[#01cd8c]">
                Success
              </Typography>
            )}
            {testState === TestState.Fail && (
              <Typography size="sm" className="al-text-[#ee140f]">
                Failed, Try Again
              </Typography>
            )}
            {testState === TestState.Uninitialized &&
              (lastSyncAt ? (
                <Stack direction="row" align="center" className="al-flex-nowrap">
                  <Typography size="xs">
                    <span className="al-text-gray">Last Sync:</span>{" "}
                    {dayjs(lastSyncAt).format("h:mm A, D MMMM YYYY")}
                  </Typography>
                  {lastSyncStatus === "success" ? (
                    <Badge variant="success" key="success">
                      Success
                    </Badge>
                  ) : lastSyncStatus === "failed" ? (
                    <Badge variant="destructive">Failed</Badge>
                  ) : lastSyncStatus === "running" ? (
                    <Badge variant="warning">Running</Badge>
                  ) : null}
                </Stack>
              ) : (
                <div></div>
              ))}

            <Stack direction="row" gap="sm">
              <Button
                variant="ghost"
                size="xs"
                className="!al-bg-white al-me-2"
                onClick={handleSync}
                disabled={!syncOpAllowed}
                aria-label="Sync dbt Cloud now"
              >
                {showSyncing ? (
                  <>
                    <IconRefresh size={20} className="al-animate-spin" />
                    <Typography size="sm">Syncing...</Typography>
                  </>
                ) : (
                  <>
                    <IconRefresh size={20} />
                    <Typography size="sm">Sync Now</Typography>
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                size="xs"
                className="!al-bg-white"
                onClick={handleTestConnection}
                aria-label="Test dbt Cloud connection"
              >
                {getTestIcon()}
                <Typography size="sm">Test</Typography>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Link>
  );
};

export default DbtCloudCard;
